import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, LogOut } from 'lucide-react';
import { callRpc } from '@/api/resources';
import { Drawer, Field } from '@/components/live';
import { useAuth } from '@/auth/AuthProvider';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';

interface OrgRow {
  organization_id: string;
  code: string;
  name: string;
  is_home: boolean;
  is_current: boolean;
  expires_at: string | null;
}

/**
 * Organisation switcher.
 *
 * Renders nothing for an ordinary user: `api.fn_my_organizations` returns exactly one row -
 * their own - so there is nothing to switch between and no hint that a cross-organisation
 * mechanism exists.
 *
 * For a platform operator it is deliberately not a quiet dropdown. Entering a customer needs a
 * reason, because that reason goes into THAT customer's audit trail, and the banner stays up
 * the whole time so nobody forgets which organisation they are looking at. The access expires
 * on its own; the server ejects them whether or not this screen is open.
 */
/** Shared by the sidebar switcher and the banner, so both read one cached answer. */
function useMyOrganizations() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: ['my-organizations'],
    queryFn: () => callRpc<OrgRow[]>('fn_my_organizations'),
    enabled: isSupabaseConfigured && Boolean(profile),
  });
}

/** Leaving is the one action offered from two places, so it lives in one hook. */
function useLeaveOrganization() {
  const { refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  return {
    busy,
    leave: async () => {
      setBusy(true);
      try {
        await callRpc('fn_leave_organization');
        queryClient.clear();
        await refreshProfile();
        toast.success('Back in your own organisation.');
      } catch (caught) {
        toast.error(caught instanceof Error ? caught.message : 'Could not leave.');
      } finally {
        setBusy(false);
      }
    },
  };
}

/**
 * The banner, rendered in the MAIN area rather than the sidebar - a warning that only appears
 * in dark chrome beside the navigation is a warning people stop seeing.
 */
export function OrgAwayBanner() {
  const orgs = useMyOrganizations();
  const { busy, leave } = useLeaveOrganization();
  const current = (orgs.data ?? []).find((row) => row.is_current);
  if (!current || current.is_home) return null;
  return (
    <div className="org-away-banner" role="status">
      You are working inside <strong>{current.name}</strong>, not your own organisation.
      Everything you do is recorded in their audit trail.
      <button disabled={busy} onClick={() => void leave()} type="button">
        <LogOut size={13} /> Leave
      </button>
    </div>
  );
}

export function OrgSwitcher() {
  const { refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const { busy: leaving, leave } = useLeaveOrganization();
  const [entering, setEntering] = useState<OrgRow | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const orgs = useMyOrganizations();
  const rows = orgs.data ?? [];
  const current = rows.find((row) => row.is_current);
  const away = current && !current.is_home;

  // One organisation means an ordinary user. Nothing to show.
  if (rows.length <= 1 && !away) return null;

  async function enter() {
    if (!entering) return;
    setError(undefined);
    if (!reason.trim()) {
      setError('Say why. It is recorded in that organisation’s audit trail.');
      return;
    }
    setBusy(true);
    try {
      await callRpc('fn_enter_organization', {
        p_organization_id: entering.organization_id,
        p_reason: reason.trim(),
      });
      // Everything cached belongs to the organisation just left.
      queryClient.clear();
      await refreshProfile();
      toast.success(`Now working in ${entering.name}.`);
      setEntering(null);
      setReason('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That organisation could not be opened.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="org-switcher">
        <span className="org-switcher-label"><Building2 size={13} /> Organisation</span>
        {rows.map((row) => (
          <button
            className={row.is_current ? 'active' : ''}
            disabled={busy || leaving || row.is_current}
            key={row.organization_id}
            onClick={() => {
              if (row.is_home) { void leave(); return; }
              setError(undefined);
              setReason('');
              setEntering(row);
            }}
            type="button"
          >
            {row.is_current && <Check size={13} />} {row.name}
            {row.is_home && <em> (yours)</em>}
          </button>
        ))}
      </div>

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setEntering(null)} type="button">Cancel</button>
            <button className="primary" disabled={busy} onClick={() => void enter()} type="button">
              {busy ? 'Opening…' : 'Enter organisation'}
            </button>
          </>
        }
        onClose={() => setEntering(null)}
        open={Boolean(entering)}
        subtitle={entering?.name}
        title="Enter another organisation"
      >
        <p className="muted" style={{ margin: 0 }}>
          You are about to work inside a customer’s data. Your reason is written to their audit
          trail alongside your name, and their administrator can read it. Access ends on its own.
        </p>
        <Field hint="What are you doing there? A ticket number is ideal." label="Reason" required>
          <input
            autoFocus
            onChange={(event) => setReason(event.target.value)}
            placeholder="Investigating the duplicate plot reported in VAN-114"
            value={reason}
          />
        </Field>
        {error && <p className="login-error" role="alert">{error}</p>}
      </Drawer>
    </>
  );
}
